import { useState, useEffect } from 'react'
import { UserRole } from '@magnetar/admin-api-interfaces'
import { useUserRolePermissionByRoleId } from '@/hooks/useUserRolePermissionByRoleId'
import { useClinicUserRolePermissionByRoleId } from '@/hooks/useClinicUserRolePermissionByRoleId'
import { useSupplierUserRolePermissionByRoleId } from '@/hooks/useSupplierUserRolePermissionByRoleId'
import { useProviderUserRolePermissionByRoleId } from '@/hooks/useProviderUserRolePermissionByRoleId'
import { useClinicGroupUserRolePermissionByRoleId } from '@/hooks/useClinicGroupUserRolePermissionByRoleId'
import { useAdSupplierGroupUserRolePermissionByRoleId } from '@/hooks/useAdSupplierGroupUserRolePermissionByRoleId'

// 權限模組標籤 - 來源：@magnetar/data-access（因 CJS/ESM 不相容，前端保留本地副本）
// System 層級
const SystemPermissionModuleLabels: Record<string, string> = {
  user: '使用者管理',
  user_role: '角色管理',
  clinic: '診所管理',
  system_version: '系統版本',
  file: '檔案管理',
  examination_room: '診室管理',
  ad_supplier: '廣告供應商',
  broadcast_device: '廣播設備',
  ad_schedule: '廣告排程',
  integration_vendor: '整合廠商',
  war_room: '戰情室',
}

// Clinic 層級
const CLINIC_PERMISSION_MODULE_LABELS: Record<string, string> = {
  user_management: '使用者管理',
  clinic_management: '診所管理',
  queue_system: '叫號系統',
  appointment_system: '預約系統',
}

// Supplier 層級
const SUPPLIER_PERMISSION_MODULE_LABELS: Record<string, string> = {
  user_management: '使用者管理',
  supplier_management: '供應商管理',
}

// Provider 層級
const PROVIDER_PERMISSION_MODULE_LABELS: Record<string, string> = {
  user_management: '用戶管理',
  clinic_management: '診所管理',
  supplier_management: '供應商管理',
  provider_settings: 'Provider 設定',
}

// Clinic Group 層級
const CLINIC_GROUP_PERMISSION_MODULE_LABELS: Record<string, string> = {
  clinic_group_user_management: '使用者管理',
  clinic_group_user_role: '角色管理',
  clinic_group_clinic_management: '診所管理',
  clinic_group_group_settings: '集團設定',
  clinic_group_broadcast_device: '廣播設備',
  clinic_group_audit_log: '操作日誌',
}

// Ad Supplier Group 層級
const AD_SUPPLIER_GROUP_PERMISSION_MODULE_LABELS: Record<string, string> = {
  ad_supplier_group_user_management: '使用者管理',
  ad_supplier_group_user_role: '角色管理',
  ad_supplier_group_supplier_management: '供應商管理',
  ad_supplier_group_group_settings: '集團設定',
  ad_supplier_group_ad_management: '廣告管理',
  ad_supplier_group_statistics_report: '統計報表',
}

import { userRolePermissionService } from '@/services/user_role_permission_service'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { AllowedAction } from '@/data/allowedActions'

interface PermissionSettingDialogProps {
  role: UserRole
  open: boolean
  onOpenChange: (open: boolean) => void
}

const actionLabels: Record<AllowedAction, string> = {
  [AllowedAction.C]: '新增',
  [AllowedAction.R]: '讀取',
  [AllowedAction.U]: '更新',
  [AllowedAction.D]: '刪除',
}

// 根據 scope_type 取得對應的模組標籤
function getModuleLabels(scopeType: string): Record<string, string> {
  switch (scopeType) {
    case 'clinic':
      return CLINIC_PERMISSION_MODULE_LABELS
    case 'ad_supplier':
      return SUPPLIER_PERMISSION_MODULE_LABELS
    case 'provider':
      return PROVIDER_PERMISSION_MODULE_LABELS
    case 'clinic_group':
      return CLINIC_GROUP_PERMISSION_MODULE_LABELS
    case 'ad_supplier_group':
      return AD_SUPPLIER_GROUP_PERMISSION_MODULE_LABELS
    default:
      return SystemPermissionModuleLabels
  }
}

// 根據 scope_type 取得對應的標題
function getScopeTitle(scopeType: string): string {
  switch (scopeType) {
    case 'clinic':
      return 'Clinic'
    case 'ad_supplier':
      return 'Supplier'
    case 'provider':
      return 'Provider'
    case 'clinic_group':
      return 'ClinicGroup'
    case 'ad_supplier_group':
      return 'AdSupplierGroup'
    default:
      return '系統'
  }
}

export function PermissionSettingDialog({
  role,
  open,
  onOpenChange,
}: PermissionSettingDialogProps) {
  const { token } = useAuth()
  const scopeType = role.scope_type || 'system'

  // 根據 scope_type 決定啟用哪個 hook
  const isClinic = scopeType === 'clinic'
  const isSupplier = scopeType === 'ad_supplier'
  const isProvider = scopeType === 'provider'
  const isClinicGroup = scopeType === 'clinic_group'
  const isAdSupplierGroup = scopeType === 'ad_supplier_group'
  const isSystem = !isClinic && !isSupplier && !isProvider && !isClinicGroup && !isAdSupplierGroup

  // 只有對應的 hook 會發送請求（其他 hook 會被禁用）
  const systemPermissions = useUserRolePermissionByRoleId(role.user_role_id, isSystem)
  const clinicPermissions = useClinicUserRolePermissionByRoleId(role.user_role_id, isClinic)
  const supplierPermissions = useSupplierUserRolePermissionByRoleId(role.user_role_id, isSupplier)
  const providerPermissions = useProviderUserRolePermissionByRoleId(role.user_role_id, isProvider)
  const clinicGroupPermissions = useClinicGroupUserRolePermissionByRoleId(
    role.user_role_id,
    isClinicGroup,
  )
  const adSupplierGroupPermissions = useAdSupplierGroupUserRolePermissionByRoleId(
    role.user_role_id,
    isAdSupplierGroup,
  )

  // 根據 scope_type 選擇使用哪個 hook 的資料
  const { permissions, isLoading, mutate } = isClinic
    ? clinicPermissions
    : isSupplier
      ? supplierPermissions
      : isProvider
        ? providerPermissions
        : isClinicGroup
          ? clinicGroupPermissions
          : isAdSupplierGroup
            ? adSupplierGroupPermissions
            : systemPermissions

  // 改為使用 module 作為 key，type 使用 AllowedAction[] 以匹配 API 回傳格式
  const [selectedActions, setSelectedActions] = useState<
    Record<string, AllowedAction[]>
  >({})

  // 用 JSON 字串作為穩定的依賴，避免陣列引用變化導致無限循環
  const permissionsKey = JSON.stringify(permissions.map(p => ({ module: p.module, allowed_action: p.allowed_action })))

  // Initialize selected actions when permissions are loaded
  useEffect(() => {
    if (permissions && permissions.length > 0) {
      const initial: Record<string, AllowedAction[]> = {}
      permissions.forEach((permission) => {
        // 使用 module 作為 key
        initial[permission.module] = permission.allowed_action as AllowedAction[]
      })
      setSelectedActions(initial)
    }
    // permissionsKey is a stable JSON serialization of `permissions`; depending on
    // `permissions` directly would re-run on every render due to array identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsKey])

  const handleActionToggle = (module: string, action: AllowedAction) => {
    setSelectedActions((prev) => {
      const current = prev[module] || []
      const updated = current.includes(action)
        ? current.filter((a) => a !== action)
        : [...current, action]
      return {
        ...prev,
        [module]: updated,
      }
    })
  }

  const handleSave = async () => {
    try {
      // 改為使用 module 而非 permission_id
      const permissionsToUpdate = Object.entries(selectedActions).map(
        ([module, actions]) => ({
          module: module as Parameters<
            typeof userRolePermissionService.batchUpdatePermissions
          >[0]['permissions'][number]['module'],
          allowed_action: actions,
        }),
      )

      let result

      switch (scopeType) {
        case 'clinic':
          result = await userRolePermissionService.batchUpdateClinicPermissions(
            {
              user_role_id: role.user_role_id,
              permissions: permissionsToUpdate,
            },
            token,
          )
          break
        case 'ad_supplier':
          result = await userRolePermissionService.batchUpdateSupplierPermissions(
            {
              user_role_id: role.user_role_id,
              permissions: permissionsToUpdate,
            },
            token,
          )
          break
        case 'provider':
          result = await userRolePermissionService.batchUpdateProviderPermissions(
            {
              user_role_id: role.user_role_id,
              permissions: permissionsToUpdate,
            },
            token,
          )
          break
        case 'clinic_group':
          result = await userRolePermissionService.batchUpdateClinicGroupPermissions(
            {
              user_role_id: role.user_role_id,
              permissions: permissionsToUpdate,
            },
            token,
          )
          break
        case 'ad_supplier_group':
          result = await userRolePermissionService.batchUpdateAdSupplierGroupPermissions(
            {
              user_role_id: role.user_role_id,
              permissions: permissionsToUpdate,
            },
            token,
          )
          break
        default:
          result = await userRolePermissionService.batchUpdatePermissions(
            {
              user_role_id: role.user_role_id,
              permissions: permissionsToUpdate,
            },
            token,
          )
      }

      if (result.success) {
        toast.success(`已更新 ${result.data?.updated || 0} 個權限設定`)
        mutate() // Refresh the permissions data
        onOpenChange(false)
      } else {
        toast.error('權限更新失敗')
      }
    } catch {
      toast.error('權限更新失敗')
    }
  }

  const moduleLabels = getModuleLabels(scopeType)
  const scopeTitle = getScopeTitle(scopeType)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>權限設定 - {role.role_name}</DialogTitle>
          <DialogDescription>
            設定此 {scopeTitle} 角色的權限。勾選代表允許該操作，取消勾選代表禁止該操作。
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1 -mx-1">
          {isLoading ? (
            <div className="space-y-4 py-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <div className="grid gap-4 py-4">
              {permissions.map((permission) => (
                <div key={permission.module} className="space-y-2">
                  {/* 使用對應的模組標籤顯示中文名稱 */}
                  <Label className="text-base">
                    {moduleLabels[permission.module] || permission.module}
                  </Label>
                  <div className="flex gap-4">
                    {Object.values(AllowedAction).map((action) => (
                      <div key={action} className="flex items-center space-x-2">
                        <Checkbox
                          id={`${permission.module}-${action}`}
                          checked={
                            selectedActions[permission.module]?.includes(action) || false
                          }
                          onCheckedChange={() =>
                            handleActionToggle(permission.module, action)
                          }
                        />
                        <Label
                          htmlFor={`${permission.module}-${action}`}
                          className="text-sm font-normal"
                        >
                          {actionLabels[action]}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={handleSave}>儲存</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
